import { dayjs } from "@boo/shared";
import type { MonthlySummaryResponse } from "@boo/shared";
import type { MonthlySummaryPageData } from "../types";
import { CATEGORY_COLORS, CATEGORY_LABELS } from "../types/category.types";

function intensityFor(amount: number): 0 | 1 | 2 | 3 | 4 {
  if (!amount) return 0;
  if (amount < 500) return 1;
  if (amount < 1200) return 2;
  if (amount < 2500) return 3;
  return 4;
}

export function mapSummaryToPageData(
  api: MonthlySummaryResponse,
  viewYear: number,
  viewMonth: number,
): Omit<
  MonthlySummaryPageData,
  "transactions" | "recurringItems" | "insights"
> {
  const today = dayjs();
  const isSameMonth =
    today.year() === viewYear && today.month() + 1 === viewMonth;
  const daysLeft = isSameMonth ? today.endOf("month").diff(today, "day") : 0;

  return {
    month: api.month,
    year: api.year,
    monthIndex: api.monthIndex,
    totalSpend: api.totalSpend,
    totalSpendCents: api.totalSpendCents,
    income: api.income,
    saved: api.saved,
    savingsRate: api.savingsRate,
    budget: 0,
    budgetUsedPercent: 0,
    daysLeft,
    biggestDayAmount: api.biggestDayAmount,
    biggestDayDate: api.biggestDayDate,
    deltaPercent: api.deltaPercent,
    deltaDirection: api.deltaDirection,
    deltaAmount: api.deltaAmount,
    dailyAverage: api.dailyAverage,
    categories: api.categories.map((c) => ({
      ...c,
      label:
        CATEGORY_LABELS[c.category as keyof typeof CATEGORY_LABELS] ??
        c.category,
      color:
        CATEGORY_COLORS[c.category as keyof typeof CATEGORY_COLORS] ??
        "#B4B2A9",
    })),
    heatmap: api.heatmap.map((day) => {
      const d = dayjs(day.date);
      const isFuture = d.isAfter(today, "day");
      return {
        date: day.date,
        amount: day.amount,
        intensity: isFuture ? 0 : intensityFor(day.amount),
        dayOfMonth: d.date(),
        isToday: d.isSame(today, "day"),
        isFuture,
        isWeekend: d.day() === 0 || d.day() === 6,
      };
    }),
  };
}
